import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import Header from './header';
import Footer from './footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCheck, faClock, faUsers, faEye, faCalendarAlt, faTimes, faChartLine, faHistory } from '@fortawesome/free-solid-svg-icons';
import { useCustomAlert } from './CustomAlert';
import { timeAgo } from '../utils/functions';
import './GroupGoals.css';

const GroupGoals = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    
    const [group, setGroup] = useState(null);
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [userRole, setUserRole] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [completionNote, setCompletionNote] = useState('');
    const [goalCompletions, setGoalCompletions] = useState([]);
    const [progressReports, setProgressReports] = useState([]);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    
    // Form states for creating goals
    const [newGoal, setNewGoal] = useState({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        frequency: 'one-off'
    });

    // Fetch group information
    const fetchGroup = async () => {
        try {
            setLoading(true);
            setError('');
            
            const requestData = { 
                user_id: user.user_id,
                status: 'active'
            };
            
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                let groupData = null;
                if (data.data && Array.isArray(data.data)) {
                    groupData = data.data.find(g => g.group_id == groupId);
                } else if (data.data && data.data.groups && Array.isArray(data.data.groups)) {
                    groupData = data.data.groups.find(g => g.group_id == groupId);
                }
                
                if (groupData) {
                    setGroup(groupData);
                } else {
                    setError('Group not found or you are not a member');
                }
            } else {
                setError(data.message || 'Failed to fetch group information');
            }
        } catch (err) {
            console.error('Error fetching group:', err);
            setError(`Failed to fetch group information: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch group goals
    const fetchGoals = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_group_goals.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group_id: groupId, 
                    user_id: user.user_id 
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setGoals(data.data.goals || []);
                setUserRole(data.data.user_role || 'member');
            } else {
                setError(data.message);
            }
        } catch (err) {
            console.error('Error fetching goals:', err);
            setError('Failed to fetch goals');
        } finally {
            setLoading(false);
        }
    };

    // Create a new goal
    const createGoal = async () => {
        if (!newGoal.title.trim() || !newGoal.description.trim() || !newGoal.start_date) {
            setError('Title, description, and start date are required');
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/create_group_goal.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    group_id: groupId,
                    admin_id: user.user_id,
                    title: newGoal.title.trim(),
                    description: newGoal.description.trim(),
                    start_date: newGoal.start_date,
                    end_date: newGoal.end_date || null,
                    frequency: newGoal.frequency
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setShowCreateModal(false);
                setNewGoal({ title: '', description: '', start_date: '', end_date: '', frequency: 'one-off' });
                await fetchGoals(); // Refresh goals list
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to create goal');
        } finally {
            setLoading(false);
        }
    };

    // Mark goal as complete
    const markGoalComplete = async () => {
        if (!completionNote.trim()) {
            setError('Please add a note when marking goal as complete');
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/mark_goal_complete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    goal_id: selectedGoal.id,
                    user_id: user.user_id,
                    note: completionNote.trim()
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setShowCompletionModal(false);
                setCompletionNote('');
                setSelectedGoal(null);
                await fetchGoals(); // Refresh goals list
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to mark goal as complete');
        } finally {
            setLoading(false);
        }
    };

    // Fetch goal completions (admin only)
    const fetchGoalCompletions = async (goal) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_goal_completions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    goal_id: goal.id,
                    user_id: user.user_id
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setGoalCompletions(data.data);
                setSelectedGoal(goal);
                setShowDetailsModal(true);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch goal completions');
        } finally {
            setLoading(false);
        }
    };

    // Close goal (admin only)
    const closeGoal = async (goal) => {
        showConfirm(
            'Close Goal',
            `Are you sure you want to close the goal "${goal.title}"? This action cannot be undone.`,
            () => performCloseGoal(goal),
            { type: 'danger', confirmText: 'Close Goal', cancelText: 'Cancel' }
        );
    };

    const performCloseGoal = async (goal) => {

        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/close_group_goal.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    goal_id: goal.id,
                    admin_id: user.user_id,
                    reason: 'Closed by admin'
                })
            });
            const data = await response.json();
            
            if (data.success) {
                await fetchGoals(); // Refresh goals list
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to close goal');
        } finally {
            setLoading(false);
        }
    };

    // Fetch progress reports (admin only)
    const fetchProgressReports = async (goal) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_goal_progress_reports.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    goal_id: goal.id,
                    user_id: user.user_id
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setProgressReports(data.data);
                setSelectedGoal(goal);
                setShowProgressModal(true);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch progress reports');
        } finally {
            setLoading(false);
        }
    };


    // Format date for display using the existing timeAgo function
    const formatDate = (dateString) => {
        return timeAgo(dateString);
    };

    // Format date range for display
    const formatDateRange = (startDate, endDate) => {
        if (!startDate) return 'No start date';
        if (!endDate) return `Started ${timeAgo(startDate)} (Ongoing)`;
        
        const startFormatted = timeAgo(startDate);
        const endFormatted = timeAgo(endDate);
        
        // Check if goal is expired using Safari-compatible date parsing
        const isoEndDate = endDate.replace(' ', 'T');
        const end = new Date(isoEndDate);
        const now = new Date();
        
        if (isNaN(end.getTime())) return 'Invalid date range';
        
        if (end < now) {
            return `${startFormatted} - ${endFormatted} (Ended)`;
        } else {
            return `${startFormatted} - ${endFormatted} (Active)`;
        }
    };

    // Check if goal is expired
    const isGoalExpired = (endDate) => {
        if (!endDate) return false;
        
        // Convert to ISO format for Safari compatibility
        const isoDateString = endDate.replace(' ', 'T');
        const end = new Date(isoDateString);
        
        if (isNaN(end.getTime())) return false;
        
        const now = new Date();
        return end < now;
    };

    // Get frequency display
    const getFrequencyDisplay = (frequency) => {
        const frequencyMap = {
            'one-off': 'One-time',
            'daily': 'Daily',
            'weekly': 'Weekly',
            'monthly': 'Monthly'
        };
        return frequencyMap[frequency] || frequency;
    };

    // Utility function to truncate text to approximately 2 lines
    const truncateText = (text, maxLength = 120) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    // Toggle description expansion
    const toggleDescription = (goalId) => {
        setExpandedDescriptions(prev => ({
            ...prev,
            [goalId]: !prev[goalId]
        }));
    };

    useEffect(() => {
        if (user && user.user_id && groupId) {
            fetchGroup();
            fetchGoals();
        }
    }, [user, groupId]);

    const renderCreateGoalModal = () => (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h5>Create New Goal</h5>
                    <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>Title *</label>
                        <input
                            type="text"
                            value={newGoal.title}
                            onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                            placeholder="Enter goal title"
                            className="form-control"
                        />
                    </div>
                    <div className="form-group">
                        <label>Description *</label>
                        <textarea
                            value={newGoal.description}
                            onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                            placeholder="Enter goal description"
                            className="form-control"
                            rows="4"
                        />
                    </div>
                    <div className="form-group">
                        <label>Start Date *</label>
                        <input
                            type="datetime-local"
                            value={newGoal.start_date}
                            onChange={(e) => setNewGoal({...newGoal, start_date: e.target.value})}
                            className="form-control"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>End Date (Optional)</label>
                        <input
                            type="datetime-local"
                            value={newGoal.end_date}
                            onChange={(e) => setNewGoal({...newGoal, end_date: e.target.value})}
                            className="form-control"
                        />
                        <small className="form-text text-muted">Leave empty for ongoing goals</small>
                    </div>
                    <div className="form-group">
                        <label>Frequency</label>
                        <select
                            value={newGoal.frequency}
                            onChange={(e) => setNewGoal({...newGoal, frequency: e.target.value})}
                            className="form-control"
                        >
                            <option value="one-off">One-time</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                        <small className="form-text text-muted">
                            {newGoal.frequency === 'one-off' ? 'Goal can be completed once' : 
                             newGoal.frequency === 'daily' ? 'Goal resets every day' :
                             newGoal.frequency === 'weekly' ? 'Goal resets every week' :
                             'Goal resets every month'}
                        </small>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </button>
                    <button className="btn btn-primary" onClick={createGoal} disabled={loading}>
                        {loading ? 'Creating...' : 'Create Goal'}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderCompletionModal = () => (
        <div className="modal-overlay" onClick={() => setShowCompletionModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h5>Mark Goal as Complete</h5>
                    <button className="close-btn" onClick={() => setShowCompletionModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    <div className="goal-preview">
                        <h6>{selectedGoal?.title}</h6>
                        <p>{selectedGoal?.description}</p>
                    </div>
                    <div className="form-group">
                        <label>Completion Note *</label>
                        <textarea
                            value={completionNote}
                            onChange={(e) => setCompletionNote(e.target.value)}
                            placeholder="Add a note about how you completed this goal..."
                            className="form-control"
                            rows="4"
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowCompletionModal(false)}>
                        Cancel
                    </button>
                    <button className="btn btn-success" onClick={markGoalComplete} disabled={loading}>
                        {loading ? 'Marking...' : 'Mark Complete'}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderDetailsModal = () => (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h5>Goal Completion Details</h5>
                    <button className="close-btn" onClick={() => setShowDetailsModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    <div className="goal-header">
                        <h6>{goalCompletions.goal?.title}</h6>
                        <div className="completion-stats">
                            <span className="stat">
                                <FontAwesomeIcon icon={faCheck} />
                                {goalCompletions.stats?.completed} completed
                            </span>
                            <span className="stat">
                                <FontAwesomeIcon icon={faUsers} />
                                {goalCompletions.stats?.total_members} total members
                            </span>
                            <span className="stat">
                                {goalCompletions.stats?.percentage}% completion rate
                            </span>
                        </div>
                    </div>
                    
                    <div className="completions-list">
                        {goalCompletions.completions?.length > 0 ? (
                            goalCompletions.completions.map(completion => (
                                <div key={completion.id} className="completion-item">
                                    <div className="completion-header">
                                        <div className="user-info">
                                            {completion.user_info.photo ? (
                                                <img 
                                                    src={completion.user_info.photo} 
                                                    alt={completion.user_info.name}
                                                    className="user-avatar"
                                                />
                                            ) : (
                                                <div className="user-avatar default">
                                                    {completion.user_info.name.charAt(0)}
                                                </div>
                                            )}
                                            <div>
                                                <h6>{completion.user_info.name}</h6>
                                                <small>@{completion.user_info.username}</small>
                                            </div>
                                        </div>
                                        <small className="completion-time">
                                            {formatDate(completion.completed_at)}
                                        </small>
                                    </div>
                                    <div className="completion-note">
                                        <p>{completion.note}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-completions">
                                <p>No completions yet</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderProgressModal = () => (
        <div className="modal-overlay" onClick={() => setShowProgressModal(false)}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h5>Progress Reports - {selectedGoal?.title}</h5>
                    <button className="close-btn" onClick={() => setShowProgressModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    <div className="goal-info">
                        <div className="goal-meta">
                            <span className="frequency-badge">
                                <FontAwesomeIcon icon={faClock} />
                                {getFrequencyDisplay(progressReports.goal?.frequency)}
                            </span>
                            <span className="date-range">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {formatDateRange(progressReports.goal?.start_date, progressReports.goal?.end_date)}
                            </span>
                        </div>
                    </div>
                    
                    <div className="recent-progress-section">
                        <h6>Recent Progress</h6>
                        <div className="progress-cards-container">
                            {progressReports.current_progress && (
                                <div className="progress-card current">
                                    <div className="progress-header">
                                        <span className="period-label">Current Period</span>
                                        <span className="completion-rate">
                                            {progressReports.current_progress.completion_percentage}%
                                        </span>
                                    </div>
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${progressReports.current_progress.completion_percentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="progress-stats">
                                        <span>{progressReports.current_progress.completed_count} / {progressReports.current_progress.total_members} completed</span>
                                    </div>
                                    <div className="period-dates">
                                        {progressReports.current_progress.interval_display || 
                                         `${formatDate(progressReports.current_progress.interval_start_date)} - ${formatDate(progressReports.current_progress.interval_end_date)}`}
                                    </div>
                                </div>
                            )}
                            
                            {progressReports.previous_progress && (
                                <div className="progress-card previous">
                                    <div className="progress-header">
                                        <span className="period-label">Previous Period</span>
                                        <span className="completion-rate">
                                            {progressReports.previous_progress.completion_percentage}%
                                        </span>
                                    </div>
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${progressReports.previous_progress.completion_percentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="progress-stats">
                                        <span>{progressReports.previous_progress.completed_count} / {progressReports.previous_progress.total_members} completed</span>
                                    </div>
                                    <div className="period-dates">
                                        {progressReports.previous_progress.interval_display || 
                                         `${formatDate(progressReports.previous_progress.interval_start_date)} - ${formatDate(progressReports.previous_progress.interval_end_date)}`}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="historical-reports-section">
                        <div className="section-header">
                            <h6>Historical Reports</h6>
                            <button className="btn btn-outline-primary btn-sm sr-only">
                                <FontAwesomeIcon icon={faHistory} className="me-1" />
                                View Full History
                            </button>
                        </div>
                        {progressReports.reports?.length > 0 ? (
                            <div className="reports-list">
                                {progressReports.reports.slice(0, 3).map((report, index) => (
                                    <div key={index} className="progress-card historical">
                                        <div className="progress-header">
                                            <span className="period-label">
                                                {formatDate(report.interval_start_date)} - {formatDate(report.interval_end_date)}
                                            </span>
                                            <span className="completion-rate">
                                                {report.completion_percentage}%
                                            </span>
                                        </div>
                                        <div className="progress-bar">
                                            <div 
                                                className="progress-fill" 
                                                style={{ width: `${report.completion_percentage}%` }}
                                            ></div>
                                        </div>
                                        <div className="progress-stats">
                                            <span>{report.completed_count} / {report.total_members} completed</span>
                                        </div>
                                    </div>
                                ))}
                                {progressReports.reports.length > 3 && (
                                    <div className="more-reports">
                                        <p className="text-muted">+{progressReports.reports.length - 3} more reports available</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="no-reports">
                                <FontAwesomeIcon icon={faHistory} size="2x" className="text-muted mb-2" />
                                <p>No historical reports available yet</p>
                                <small className="text-muted">Historical reports will appear here as the goal progresses</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    if (!user || !user.user_id) {
        return <div className="container-fluid p-3">
            <div className="alert alert-warning" role="alert">
                <h5>Authentication Required</h5>
                <p>Please log in to access group goals.</p>
            </div>
        </div>;
    }

    if (loading && !group) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading group goals...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }


    return (
        <div className="h-100 d-flex flex-column">
            <Header />
            <div className="flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>
                <div className="container-fluid p-3">
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex align-items-center mb-3">
                                <div>
                                    <h2 className="mb-0">🎯 Group Goals - {group?.name}</h2>
                                    <p className="text-muted mb-0">Track and complete group objectives together</p>
                                </div>
                                <div className="ms-auto d-flex gap-2">
                                {userRole === 'admin' && (
                                    <button 
                                            className="btn btn-primary"
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                        Create Goal
                                    </button>
                                )}
                                </div>
                            </div>

                            {error && <div className="alert alert-danger">{error}</div>}

                            <div className="goals-container">
                                {goals.length > 0 ? (
                                    <div className="row">
                                        {goals.map(goal => (
                                            <div key={goal.id} className="col-md-6 col-lg-4 mb-4">
                                                <div className="goal-card">
                                                    <div className="goal-header">
                                                        <h5 className="goal-title">{goal.title}</h5>
                                                        <div className="goal-meta">
                                                            <div className="frequency-badge">
                                                                <FontAwesomeIcon icon={faClock} />
                                                                {getFrequencyDisplay(goal.frequency)}
                                                            </div>
                                {goal.current_interval && (
                                    <div className="current-interval">
                                                                <FontAwesomeIcon icon={faCalendarAlt} />
                                        Current: {goal.current_interval.display}
                                        {goal.current_interval.completion_stats && (
                                            <span className="completion-stats">
                                                ({goal.current_interval.completion_stats.completed_count}/{goal.current_interval.completion_stats.total_members})
                                            </span>
                                        )}
                                    </div>
                                )}
                                {goal.previous_interval && (
                                    <div className="previous-interval">
                                        <FontAwesomeIcon icon={faHistory} />
                                        Previous: {goal.previous_interval.display}
                                        {goal.previous_interval.completion_stats && (
                                            <span className="completion-stats">
                                                ({goal.previous_interval.completion_stats.completed_count}/{goal.previous_interval.completion_stats.total_members})
                                            </span>
                                        )}
                                                            </div>
                                                        )}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="goal-description">
                                                        <p className="goal-description-text">
                                                            {expandedDescriptions[goal.id] ? goal.description : truncateText(goal.description)}
                                                        </p>
                                                        {goal.description && goal.description.length > 120 && (
                                                            <button 
                                                                className="read-more-btn"
                                                                onClick={() => toggleDescription(goal.id)}
                                                            >
                                                                {expandedDescriptions[goal.id] ? 'Read Less' : 'Read More'}
                                                            </button>
                                                        )}
                                                        <div className="goal-dates">
                                                            <small className="text-muted">
                                                                {formatDateRange(goal.start_date, goal.end_date)}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="goal-stats">
                                                        <div className="completion-bar">
                                                            <div 
                                                                className="completion-fill" 
                                                                style={{ width: `${goal.completion_stats.percentage}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="stats-text">
                                                            {goal.completion_stats.completed} / {goal.completion_stats.total} completed
                                                            ({goal.completion_stats.percentage}%)
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="goal-actions">
                                                        {userRole === 'admin' ? (
                                                            <div className="admin-actions">
                                                                <div className="d-flex flex-wrap gap-2 mb-2">
                                                                    <button 
                                                                        className="btn btn-outline-primary btn-sm"
                                                                        onClick={() => fetchGoalCompletions(goal)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEye} className="me-1" />
                                                                        Details
                                                                    </button>
                                                                    <button 
                                                                        className="btn btn-outline-info btn-sm"
                                                                        onClick={() => fetchProgressReports(goal)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faChartLine} className="me-1" />
                                                                        Reports
                                                                    </button>
                                                                    <button 
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => closeGoal(goal)}
                                                                        title="Close this goal"
                                                                    >
                                                                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                                                                        Close
                                                                    </button>
                                                                </div>
                                                                {/* Admin can also mark their own goal as complete */}
                                                                {!goal.user_has_completed && !isGoalExpired(goal.end_date) ? (
                                                                    <button 
                                                                        className="btn btn-success btn-sm"
                                                                        onClick={() => {
                                                                            setSelectedGoal(goal);
                                                                            setShowCompletionModal(true);
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                                        Mark My Goal Complete
                                                                    </button>
                                                                ) : goal.user_has_completed ? (
                                                                    <span className="completed-badge">
                                                                        <FontAwesomeIcon icon={faCheck} />
                                                                        You Completed This
                                                                    </span>
                                                                ) : (
                                                                    <span className="expired-badge">
                                                                        <FontAwesomeIcon icon={faClock} />
                                                                        Expired
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            !goal.user_has_completed && !isGoalExpired(goal.end_date) ? (
                                                                <button 
                                                                    className="btn btn-success btn-sm"
                                                                    onClick={() => {
                                                                        setSelectedGoal(goal);
                                                                        setShowCompletionModal(true);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                                    Mark Complete
                                                                </button>
                                                            ) : goal.user_has_completed ? (
                                                                <span className="completed-badge">
                                                                    <FontAwesomeIcon icon={faCheck} />
                                                                    Completed
                                                                </span>
                                                            ) : (
                                                                <span className="expired-badge">
                                                                    <FontAwesomeIcon icon={faClock} />
                                                                    Expired
                                                                </span>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faCheck} size="3x" className="text-muted mb-3" />
                                        <h5 className="text-muted">No Goals Yet</h5>
                                        <p className="text-muted">
                                            {userRole === 'admin' 
                                                ? 'Create the first goal to get started!' 
                                                : 'No goals have been created for this group yet.'
                                            }
                                        </p>
                                        {userRole === 'admin' && (
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => setShowCreateModal(true)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                Create First Goal
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            
            {/* Modals */}
            {showCreateModal && renderCreateGoalModal()}
            {showCompletionModal && renderCompletionModal()}
            {showDetailsModal && renderDetailsModal()}
            {showProgressModal && renderProgressModal()}
            
            {/* Custom Alert Component */}
            <AlertComponent />
        </div>
    );
};

export default GroupGoals;
